"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { scrollToFirstError } from "@wtc/ui";
import { zodResolver } from "@/lib/utils/validation/zod-react-hook-form-resolver";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import { DateTimePicker } from "@/components/ui/date/DateTimePicker";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, MapPin, Users, Plus, X } from "lucide-react";
import { MeetingDescription } from "./components/form-fields/MeetingDescription";
import { MeetingTypeAndStatusFields } from "./components/form-fields/MeetingTypeAndStatusFields";
import { TimeRangeFields } from "./components/form-fields/TimeRangeFields";
import { MeetingLocationField } from "./components/form-fields/MeetingLocationField";
import { AttendeesSection } from "./components/form-fields/AttendeesSection";
import { MeetingAttendees } from "./components/MeetingAttendees";
import { MeetingStatusPreview } from "./components/MeetingStatusPreview";
import { MeetingNotes } from "./components/form-fields/MeetingNotes";
import { MeetingTitle } from "./components/form-fields/MeetingTitle";
import { meetingSchema } from "./schemas/meeting-schema";
import { MEETING_TYPE_OPTIONS, MEETING_STATUS_OPTIONS } from "./constants/meeting-constants";
import type { MeetingFormProps, MeetingFormValues } from "./types/meeting-form";

export const MeetingForm: React.FC<MeetingFormProps> = ({
  contactId,
  userId,
  onSubmit,
  isSubmitting,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [attendees, setAttendees] = useState<string[]>([]);
  const [newAttendee, setNewAttendee] = useState("");

  const form = useForm<MeetingFormValues>({
    resolver: zodResolver(meetingSchema as any),
    defaultValues: {
      title: "",
      description: "",
      start_time: new Date(),
      end_time: new Date(Date.now() + 60 * 60 * 1000), // 1 hour from now
      meeting_type: "call",
      status: "scheduled",
      location: "",
      attendees: [],
      notes: "",
    },
  });

  const watchedMeetingType = form.watch("meeting_type");
  const watchedStatus = form.watch("status");

  const handleSubmit = async (values: MeetingFormValues) => {
    setError(null);
    try {
      // Include attendees in the submission
      const meetingData = {
        ...values,
        attendees,
      };
      await onSubmit(meetingData);
      form.reset();
      setAttendees([]);
    } catch (err: any) {
      setError(err.message || "Failed to create meeting");
    }
  };

  const handleAddAttendee = () => {
    if (newAttendee.trim() && !attendees.includes(newAttendee.trim())) {
      setAttendees([...attendees, newAttendee.trim()]);
      setNewAttendee("");
    }
  };

  const handleRemoveAttendee = (attendeeToRemove: string) => {
    setAttendees(attendees.filter((attendee) => attendee !== attendeeToRemove));
  };

  // Auto-set end time to 1 hour after start time when start time changes
  const handleStartTimeChange = (startTime: Date | undefined) => {
    if (startTime) {
      form.setValue("start_time", startTime);
      const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // 1 hour later
      form.setValue("end_time", endTime);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit, scrollToFirstError)} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg dark:bg-red-950/40 dark:text-red-300">
            <AlertCircle size={16} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Title */}
        <MeetingTitle control={form.control} isSubmitting={isSubmitting} />

        {/* Type and Status Row */}
        <MeetingTypeAndStatusFields
          control={form.control}
          isSubmitting={isSubmitting}
          meetingTypeOptions={MEETING_TYPE_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          statusOptions={MEETING_STATUS_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
        />

        {/* Time Range */}
        <TimeRangeFields
          control={form.control}
          isSubmitting={isSubmitting}
          onStartTimeChange={handleStartTimeChange}
        />

        {/* Location (conditional based on meeting type) */}
        {(watchedMeetingType === "in_person" ||
          watchedMeetingType === "video_call" ||
          watchedMeetingType === "other") && (
          <MeetingLocationField
            control={form.control}
            isSubmitting={isSubmitting}
            meetingType={watchedMeetingType}
          />
        )}

        <Separator />

        {/* Description */}
        <MeetingDescription control={form.control} isSubmitting={isSubmitting} />

        {/* Attendees */}
        <AttendeesSection
          newAttendee={newAttendee}
          setNewAttendee={setNewAttendee}
          onAddAttendee={handleAddAttendee}
          attendees={attendees}
          onRemoveAttendee={handleRemoveAttendee}
          isSubmitting={isSubmitting}
        />

        {/* Notes (for completed meetings) */}
        {watchedStatus === "completed" && (
          <MeetingNotes control={form.control} isSubmitting={isSubmitting} />
        )}

        {/* Status Preview */}
        <MeetingStatusPreview status={watchedStatus} type={watchedMeetingType} />

        {/* Submit Button */}
        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={isSubmitting} className="min-w-[120px]">
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">Creating...</span>
              </>
            ) : watchedStatus === "completed" ? (
              "Log Meeting"
            ) : (
              "Schedule Meeting"
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};
